<template>
  <Typography tag="td" color="gray800">
    <div class="meeting-wrapper">
      <!-- <div :class="['meeting-item', { team: !!agenda.team_uid }]"> -->
      <div :class="['meeting-item']">
        <div class="meeting-items-flex">
          <span class="meeting-item-col" v-if="agenda.team_uid">
            <typography color="gray700" weight="600" transform="uppercase">Team</typography>
            <typography color="gray700" class="pl-3" weight="400" transform="capitalize">
              {{ agenda.team_name }}
            </typography>
          </span>
          <span class="meeting-item-col">
            <typography color="gray700" weight="600" transform="uppercase">Parent</typography>
            <typography color="gray700" class="pl-3" weight="400" transform="capitalize">
              {{ agenda.parent_name }}
            </typography>
          </span>
          <span class="meeting-item-col">
            <typography color="gray700" weight="600" transform="uppercase"> Student </typography>
            <typography color="gray700" class="pl-3" weight="400" transform="capitalize">
              <div>{{ agenda.child_name }} <span style="font-size: small" v-if="agenda.child_description"> ( {{ agenda.child_description }} )</span></div> 
            </typography>
          </span>
          <span class="meeting-item-col">
            <typography color="gray700" weight="600" transform="uppercase">
              Meeting Type
            </typography>
            <div class="pl-3" v-if="authStore.me.modify_schedule && !archived">
              <v-menu
                v-model="state.showMeetingTypeMenu"
                location="bottom"
              >
                <template v-slot:activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    variant="text"
                    size="small"
                    :loading="state.loadingMeetingType"
                    class="pa-0 text-capitalize"
                    style="min-width: auto; text-transform: capitalize;"
                  >
                    {{ formatMeetingTypeLabel(agenda.meeting_type) }}
                    <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="type in getAvailableMeetingTypes(agenda.meeting_types)"
                    :key="type.value"
                    @click="updateMeetingType(type.value)"
                    :active="agenda.meeting_type === type.value"
                  >
                    <v-list-item-title>{{ type.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <typography v-else color="gray700" class="pl-3" weight="400" transform="capitalize">
              {{ parseMeetingType(agenda) }}
            </typography>
          </span>
          <MeetingTypeView :conference-day="conferenceDay" :agenda="agenda"></MeetingTypeView>
          <span class="meeting-item-col" v-if="agenda.translator_language">
            <typography color="gray700" weight="600" transform="uppercase">Translator</typography>
            <typography color="gray700" class="pl-3" weight="400" transform="capitalize">
              {{ getLanguageName(agenda.translator_language) }}
            </typography>
          </span>
        </div>
        <div class="delete-btn" v-if="authStore.me.modify_schedule">
          <v-btn
            color="primary"
            variant="text"
            icon
            size="small"
            @click="deleteSchedule"
            :loading="state.loadingDelete"
            :disabled="archived"
          >
            <v-icon color="primary">mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </Typography>
  <td v-if="showComments">
    <div class="comment-preview" v-if="agenda.comment">
      <p>{{ agenda.comment }}</p>
      <v-btn
        icon
        size="small"
        variant="text"
        @click="state.comment = agenda.comment!"
        data-cy="comment-view-btn"
      >
        <v-tooltip activator="parent" class="custom-tooltip" location="bottom">
          {{ agenda.comment }}
        </v-tooltip>
        <v-icon icon="mdi-message-outline" size="1rem" color="primary"> </v-icon>
      </v-btn>
    </div>
  </td>
  <td v-if="noShow">
    <div class="noshow-wrapper">
      <v-switch
        class="custom-input"
        :model-value="agenda.attended == 0"
        @update:model-value="updateNoShow(agenda)"
        density="compact"
        color="primary"
        :loading="state.loadingNoShow"
        :disabled="state.loadingNoShow"
      ></v-switch>
    </div>
  </td>
  <ModalMeetingComment
    v-if="state.comment"
    :comment="state.comment"
    :show="true"
    @close="state.comment = ''"
  >
  </ModalMeetingComment>
</template>

<script setup lang="ts">
import type { ConferenceDay, GetSchedule } from "@/api_types/teacher";
import { useAuthenticationStore } from "@/stores/authentication";
import { reactive } from "vue";
import Typography from "../typography.vue";
import ModalMeetingComment from "@/components/teacher/modal/MeetingComment.vue";
import FetchService from "@/services/fetch.service";
import MeetingTypeView from "../global/MeetingTypeView.vue";
import { getLanguageName } from "@/composables/useLanguage.js";

const props = defineProps<{
  agenda: GetSchedule;
  noShow: boolean;
  reload: () => any;
  delete: () => any;
  showComments: boolean;
  archived: boolean;
  conferenceDay: ConferenceDay;
}>();
const parseMeetingType = (type) => {
  console.log(type);
  if (type.meeting_type === null) {
    const trueEntries = Object.entries(type.meeting_types).filter(([key, value]) => value === true);

    if (trueEntries.length === 1) {
      type.meeting_type = trueEntries[0][0];
    }
  }

  
  if (type.meeting_type === "in_person") return "In Person";
  return type.meeting_type || "Not Available";
};

const formatMeetingTypeLabel = (type: string | null): string => {
  if (!type) return "Not Available";
  if (type === "in_person") return "In Person";
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const getAvailableMeetingTypes = (meetingTypes: any): Array<{ value: string; label: string }> => {
  if (!meetingTypes) return [];
  
  // Handle case where meeting_types might be a string
  const types = typeof meetingTypes === "string" ? JSON.parse(meetingTypes) : meetingTypes;
  
  const availableTypes: Array<{ value: string; label: string }> = [];
  
  if (types.in_person) availableTypes.push({ value: "in_person", label: "In Person" });
  if (types.virtual) availableTypes.push({ value: "virtual", label: "Virtual" });
  if (types.phone) availableTypes.push({ value: "phone", label: "Phone" });
  if (types.callback) availableTypes.push({ value: "callback", label: "Callback" });
  
  return availableTypes;
};

const authStore = useAuthenticationStore();

const state = reactive({
  loadingNoShow: false,
  comment: "",
  loadingDelete: false,
  loadingMeetingType: false,
  showMeetingTypeMenu: false
});

const updateNoShow = async (meeting: GetSchedule) => {
  if (!props.noShow) return;
  state.loadingNoShow = true;
  // @ts-ignore
  const val = meeting.attended == 0 ? 1 : 0;
  // @ts-ignore
  meeting.attended = 0;
  await FetchService.fetchData(
    `/schedule/unattended/${props.agenda.pta_id}`,
    val ? "delete" : "put"
  );
  // @ts-ignore
  meeting.attended = val;
  state.loadingNoShow = false;
};

const deleteSchedule = async () => {
  state.loadingDelete = true;
  const resp = await FetchService.fetchData("/schedule/" + props.agenda.pta_id, "delete");
  if (resp) {
    props.delete();
  } else {
    props.reload();
  }
  state.loadingDelete = false;
};

const updateMeetingType = async (meetingType: string) => {
  if (!authStore.me.modify_schedule || props.archived) return;
  
  state.loadingMeetingType = true;
  state.showMeetingTypeMenu = false;
  
  const details = {
    pta_id: props.agenda.pta_id,
    meeting_type: meetingType
  };
  
  const resp = await FetchService.fetchData(
    `/meetingdetail/${props.agenda.pta_id}`,
    "put",
    details
  );
  
  if (resp) {
    // Update local state
    // @ts-ignore
    props.agenda.meeting_type = meetingType;
    props.reload();
  }
  
  state.loadingMeetingType = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.meeting-wrapper {
  display: flex;
  align-items: center;
  min-height: 45px;
  gap: 10px;
  flex-wrap: wrap;

  .meeting-item {
    display: flex;
    justify-content: space-between;
    min-height: 45px;
    background-color: $primary-25;
    border: 1px solid $primary-75;
    flex: 1;
    align-items: center;
    border-radius: 4px;
    padding: 0 10px;
    width: 100%;

    &.team {
      background-color: $primary-100;
    }
  }
}

.noshow-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  .custom-input {
    max-width: fit-content;
  }
}

.comment-preview {
  vertical-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    margin: 0;
    max-width: 4rem !important;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.meeting-items-flex {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  gap: 1px 13px;
  :deep(.meeting-item-col) {
    display: flex;
    > span {
      white-space: nowrap;
    }
  }
}
.delete-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
</style>
